/**
 * Sales Orders Route Loaders
 * TanStack Router loaders that populate Redux store
 */

import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { parseUrlFilter } from '@spcc/core'
import { salesOrdersApi } from '../api/sales-orders-api'
import { salesOrdersActions } from '../../store/sales-orders.slice'
import type { SalesOrdersFilter } from '../../domain/types'
import { salesOrdersFilterConfig } from '../../domain/filter-config'

interface RouterContext {
  store: {
    dispatch: Dispatch<UnknownAction>
    getState: () => unknown
  }
}

interface SalesOrdersStoreState {
  salesOrders: { filter: SalesOrdersFilter }
}

/**
 * Compare two filter objects by value (key-order-independent).
 */
function filtersAreEqual(a: SalesOrdersFilter, b: SalesOrdersFilter): boolean {
  const sorted = (obj: Record<string, unknown>) =>
    Object.keys(obj)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => { acc[k] = obj[k]; return acc }, {})
  return JSON.stringify(sorted(a as never)) === JSON.stringify(sorted(b as never))
}

/**
 * Parse the current browser URL into a filter.
 * Runs at route navigation time (not on within-route URL changes).
 */
function parseFilterFromUrl(): SalesOrdersFilter {
  const params: Record<string, string | null> = {}
  if (typeof window !== 'undefined' && window.location.search) {
    new URLSearchParams(window.location.search).forEach((v, k) => { params[k] = v })
  }
  return parseUrlFilter(params, salesOrdersFilterConfig)
}

/**
 * Loader for sales orders list page.
 * Runs ONCE per route navigation (no loaderDeps), so pagination URL changes
 * do NOT re-trigger this loader and do NOT reset the page.
 */
export const salesOrdersLoader = async ({ context, initialSearch }: { context: RouterContext; initialSearch?: Record<string, string | undefined> }) => {
  if (!context?.store) return null

  const { store } = context
  const currentFilter = (store.getState() as SalesOrdersStoreState).salesOrders?.filter

  // Parse filter from initialSearch (route deps) or fall back to window.location.search
  let urlFilter: SalesOrdersFilter
  if (initialSearch && Object.keys(initialSearch).some(k => initialSearch[k] !== undefined)) {
    const params: Record<string, string | null> = {}
    for (const [key, value] of Object.entries(initialSearch)) {
      params[key] = value ?? null
    }
    urlFilter = parseUrlFilter(params, salesOrdersFilterConfig)
  } else {
    urlFilter = parseFilterFromUrl()
  }

  const initialFilter: SalesOrdersFilter = {
    ...urlFilter,
    factoryIds: currentFilter?.factoryIds ?? urlFilter.factoryIds,
  }

  // Skip dispatch if nothing changed — component's useEffect triggers the fetch.
  if (currentFilter && filtersAreEqual(currentFilter, initialFilter)) {
    return null
  }

  store.dispatch(salesOrdersActions.setFilter(initialFilter))
  return null
}

/**
 * Loader for single sales order detail
 * Can be used for sales order detail page/modal
 */
export const salesOrderDetailLoader = async ({
  context,
  params,
}: {
  context: RouterContext
  params: { salesOrderId: string }
}) => {
  const { store } = context

  try {
    const salesOrder = await salesOrdersApi.getSalesOrder(params.salesOrderId)
    if (salesOrder) {
      store.dispatch(salesOrdersActions.setSelectedSalesOrder(salesOrder))
    }
    return null
  } catch (error) {
    store.dispatch(salesOrdersActions.setError((error as Error).message))
    return null
  }
}

// Legacy aliases for backwards compatibility
export const contractsLoader = salesOrdersLoader
export const contractDetailLoader = salesOrderDetailLoader
